import { normalizePhoneUS } from "@/lib/forms/phone";

export const QQ_STORAGE_KEY = "restore_quick_quote_lead";
export const QQ_NORWOOD_KEY = "restore_quick_quote_norwood";

export type LeadDataFields = Partial<{
  em: string;
  ph: string;
  fn: string;
  ln: string;
  ct: string;
  st: string;
  zp: string;
}>;

async function sha256(value: string): Promise<string> {
  const data = new TextEncoder().encode(value);
  const buf = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(buf)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Fire a Zaraz custom event with the raw lead field values plus SHA-256 hashes
 * for enhanced conversions. Called once on a successful form submission, before
 * the redirect. Tracking failures are swallowed so they never block the funnel.
 *
 * Google Ads expects enhanced-conversion hashes under the
 * user_data.sha256_email_address / user_data.sha256_phone_number keys, so we
 * name the properties exactly that way. Raw PII fields are kept here only for
 * verification in the Zaraz debugger; once the mapping is confirmed, remove
 * em, ph, fn, ln, zp and turn off "Include Event Properties" in Zaraz.
 */
export const QQ_LEAD_FIRED_KEY = "restore_lead_fired";

/**
 * The Google Ads enhanced-conversion parameter, in the exact form Google's own
 * tag sends: one "em" value, "tv.1~em.<sha256>~pn.<sha256>", hex hashes.
 *
 * Zaraz forwards every event property to Google as a URL parameter under its
 * own name, so the user_data.* keys arrive as parameters Google ignores. Google
 * reads only "em". Returns "" when neither hash is present, so the caller can
 * leave the parameter out rather than send an empty one.
 */
export function enhancedConversionParam(emailHash: string, phoneHash: string): string {
  const parts = [emailHash && `em.${emailHash}`, phoneHash && `pn.${phoneHash}`].filter(Boolean);
  return parts.length ? `tv.1~${parts.join("~")}` : "";
}

/**
 * Mark that a quick quote / lead form was submitted this session. The Acuity
 * conversion listener reads this to skip the Google Ads consultation_booked
 * conversion for these visitors — their consult is attributed via the offline
 * conversion import instead.
 */
export function rememberLeadFired() {
  try {
    window.sessionStorage.setItem(QQ_LEAD_FIRED_KEY, "1");
  } catch {
    /* ignore */
  }
}

export async function trackLeadEvent(fields: LeadDataFields) {
  if (typeof window === "undefined") return;
  // The form was submitted — flag it even if Zaraz hasn't loaded yet, since
  // these leads are attributed offline regardless of the browser event.
  rememberLeadFired();
  try {
    const zaraz = (window as unknown as { zaraz?: { track?: (n: string, p: unknown) => void } })
      .zaraz;
    if (typeof zaraz?.track !== "function") return;

    const emailNormalized = fields.em?.trim().toLowerCase() ?? "";
    const phoneNormalized = fields.ph ? normalizePhoneUS(fields.ph.trim()) : "";

    let emailHash = "";
    let phoneHash = "";
    try {
      if (emailNormalized) emailHash = await sha256(emailNormalized);
      if (phoneNormalized) phoneHash = await sha256(phoneNormalized);
    } catch {
      /* hashing is best-effort; still fire the event if hashes are empty */
    }

    // Remember the identifiers so later conversions (e.g. an Acuity booking
    // in the same session) can also send enhanced-conversion data.
    try {
      if (emailNormalized) window.sessionStorage.setItem("rh_email", emailNormalized);
      if (phoneNormalized) window.sessionStorage.setItem("rh_phone", phoneNormalized);
    } catch {
      /* ignore */
    }

    // Google flags the conversion as "missing user-provided data" when the
    // properties are present but empty, so only send the ones we actually have.
    const userData: Record<string, string> = {};
    if (emailHash) userData["user_data.sha256_email_address"] = emailHash;
    if (phoneHash) userData["user_data.sha256_phone_number"] = phoneHash;
    if (Object.keys(userData).length === 0) return;
    userData.em = enhancedConversionParam(emailHash, phoneHash);

    zaraz.track("google_ads_lead", userData);
  } catch {
    /* tracking must never block the form */
  }
}

/** Split a full name into first / last for the stored lead data. */
export function splitLeadName(full: string): { fn: string; ln: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { fn: "", ln: "" };
  if (parts.length === 1) return { fn: parts[0], ln: "" };
  return { fn: parts[0], ln: parts.slice(1).join(" ") };
}

/** Remember the Norwood class a visitor picked before landing on step 1. */
export function rememberNorwood(label: string) {
  try {
    window.sessionStorage.setItem(QQ_NORWOOD_KEY, label);
  } catch {
    /* ignore */
  }
}

/** Read the Norwood class from session storage. */
export function readNorwood(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(QQ_NORWOOD_KEY);
  } catch {
    return null;
  }
}

export function clearNorwood() {
  try {
    window.sessionStorage.removeItem(QQ_NORWOOD_KEY);
  } catch {
    /* ignore */
  }
}

export const QQ_HEARD_OPTIONS = [
  "Youtube",
  "Past Patient",
  "Yelp",
  "Television",
  "Social Media",
  "Referral",
  "Billboard",
  "Google Search",
  "Radio",
  "Other",
];

export const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "District of Columbia", "Florida", "Georgia",
  "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota",
  "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island",
  "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
  "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming",
];

export const qqInputCls =
  "w-full border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary";
export const qqLabelCls = "mb-1 block text-sm font-semibold text-foreground";

export const QQ_PILOT_KEY = "rh_pilot";

/**
 * Charlotte pilot: remember that the visitor entered the funnel from
 * /restore-charlotte so the final step can show the Charlotte booking
 * calendar instead of the national one.
 */
export function rememberPilot(market: string) {
  try {
    window.sessionStorage.setItem(QQ_PILOT_KEY, market);
  } catch {
    /* ignore */
  }
}

export function readPilot(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(QQ_PILOT_KEY);
  } catch {
    return null;
  }
}

/** Remember the buffered lead id so page 2 can attach the address to it. */
export function rememberLeadId(id: string) {
  try {
    window.sessionStorage.setItem(QQ_STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

/**
 * Remember the lead's phone (E.164) so the Acuity conversion listener on the
 * scheduling pages can hash it for enhanced conversions — Acuity's postMessage
 * no longer includes the phone number. Same-origin sessionStorage only.
 */
export function rememberPhone(phone: string) {
  const normalized = normalizePhoneUS(phone.trim());
  if (!normalized) return;
  try {
    window.sessionStorage.setItem("rh_phone", normalized);
  } catch {
    /* ignore */
  }
}

/** Read the lead id from the URL (?qq=) first, then session storage. */
export function readLeadId(): string | null {
  if (typeof window === "undefined") return null;
  const fromUrl = new URLSearchParams(window.location.search).get("qq");
  if (fromUrl) return fromUrl;
  try {
    return window.sessionStorage.getItem(QQ_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearLeadId() {
  try {
    window.sessionStorage.removeItem(QQ_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}